import React from "react";
import { Button as RawButton } from "@/components/ui/button";
import { Card as RawCard } from "@/components/ui/card";
import {
  CheckCircle2,
  Calendar,
  Clock,
  Users,
  Ship,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { motion } from "framer-motion";

/**
 * @typedef {Object} FerryConfirmation
 * @property {{ image: string; operator: string; origin_port: string; destination_port: string }} ferry
 * @property {Date} date
 * @property {Date} [returnDate]
 * @property {string} departureTime
 * @property {string} [returnDepartureTime]
 * @property {number} passengers
 * @property {string} [vehicleType]
 * @property {boolean} isRoundTrip
 * @property {number} totalAmount
 */

/**
 * @param {{
 *   confirmation: FerryConfirmation,
 *   onBackToSearch: () => void,
 *   onProceedToTransport: () => void,
 *   onCompleteBooking: () => void,
 *   showNextStep: boolean
 * }} props
 */
export default function FerryConfirmationView({
  confirmation,
  onBackToSearch,
  onProceedToTransport,
  onCompleteBooking,
  showNextStep,
}) {
  const {
    ferry,
    date,
    returnDate,
    departureTime,
    returnDepartureTime,
    passengers,
    vehicleType,
    isRoundTrip,
    totalAmount,
  } = confirmation;

  const Button = /** @type {React.ComponentType<any>} */ (RawButton);
  const Card = /** @type {React.ComponentType<any>} */ (RawCard);
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl w-full"
      >
        <Card className="p-8 sm:p-12 text-center bg-white border border-gray-100 rounded-2xl shadow-md">
          {/* Success Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5, delay: 0.2, type: "spring" }}
            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-[#0B2421]"
          >
            <CheckCircle2 className="h-12 w-12 text-[#D4AF37]" />
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0B2421] mb-3">
              Ferry Selection Confirmed!
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Your ferry has been reserved
            </p>
          </motion.div>

          {/* Booking Details */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="text-left space-y-4 mb-8"
          >
            <div className="flex items-start gap-3">
              <div className="w-24 h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-blue-400 to-blue-600">
                <img
                  src={ferry.image}
                  alt={ferry.operator}
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <div className="flex items-center gap-1 text-sm text-gray-600 mb-2">
                  <Ship className="h-4 w-4" />
                  <span>{ferry.operator}</span>
                </div>
                <div className="flex items-center gap-2 font-semibold text-gray-600">
                  <span className="text-sm">{ferry.origin_port}</span>
                  <ArrowRight className="h-4 w-4" />
                  <span className="text-sm">{ferry.destination_port}</span>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-gray-100">
              <div className="text-sm text-gray-600">
                Trip Type:{" "}
                <span className="font-semibold text-gray-600">
                  {isRoundTrip ? "Round-Trip" : "One-Way"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calendar className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">
                      Departure
                    </div>
                    <div className="font-semibold text-[#0B2421]">
                      {format(date, "dd MMM yyyy")}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">
                      Depart Time
                    </div>
                    <div className="font-semibold text-[#0B2421]">
                      {departureTime}
                    </div>
                  </div>
                </div>

                {isRoundTrip && (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Calendar className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">
                          Return
                        </div>
                        <div className="font-semibold text-[#0B2421]">
                          {returnDate ? format(returnDate, "dd MMM yyyy") : ""}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                        <Clock className="h-5 w-5 text-[#D4AF37]" />
                      </div>
                      <div>
                        <div className="text-xs text-gray-600">
                          Return Time
                        </div>
                        <div className="font-semibold text-[#0B2421]">
                          {returnDepartureTime}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users className="h-5 w-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">
                      Passengers
                    </div>
                    <div className="font-semibold text-[#0B2421]">
                      {passengers}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0B2421] rounded-lg flex items-center justify-center flex-shrink-0">
                    <div className="text-xs font-bold text-[#D4AF37]">
                      SGD
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-600">
                      Subtotal
                    </div>
                    <div className="font-semibold text-[#0B2421]">
                      SGD {totalAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {vehicleType && vehicleType !== "none" && (
              <div className="pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-600">
                  Vehicle:{" "}
                  <span className="font-semibold text-gray-600 capitalize">
                    {vehicleType}
                  </span>
                </div>
              </div>
            )}
          </motion.div>

          {/* Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
            className="space-y-3"
          >
            <Button
              onClick={onCompleteBooking}
              className="w-full h-14 bg-[#0B2421] hover:bg-[#0B2421]/85 text-white font-semibold text-lg"
            >
              Complete Booking Now
            </Button>
            {showNextStep && (
              <Button
                onClick={onProceedToTransport}
                className="w-full h-14 font-semibold text-lg bg-gray-100 text-[#0B2421]"
              >
                Continue to Transportation
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onBackToSearch}
              className="w-full h-10 text-gray-600 hover:bg-transparent hover:underline hover:text-[#0B2421]/70"
            >
              Change Ferry Selection
            </Button>
          </motion.div>

          {/* Info Notice */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="mt-6 p-4 border border-amber-200 bg-amber-50 rounded-xl"
          >
            <p className="text-sm text-amber-700">
              💡 Complete now or add land transport for your trip
            </p>
          </motion.div>
        </Card>
      </motion.div>
    </div>
  );
}
